/**
 * Hook for fetching association types from the backend schema.
 */

import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import {
  getPersistedAssociationTypes,
  setPersistedAssociationTypes,
} from '@/lib/localStorage/persistedAssociationTypes';

import { type AssociationType, createSchemaClient } from '../core/SchemaClient';
import { formatAssociationName } from '../list/utils/getColumnLabel';

const EMPTY_ASSOCIATION_TYPES: AssociationType[] = [];

/**
 * Canonical React Query key for the org-wide association-type schema list.
 * Every reader or writer of this cache entry uses it, so a fresh copy fetched
 * elsewhere (see usePipelineStages' schema refresh) lands where this hook reads.
 */
export function associationTypesQueryKey(teamId: number | undefined) {
  return ['association-types', teamId] as const;
}

/**
 * Hook to fetch all association types from the backend schema.
 *
 * The data is cached globally per team for efficient reuse across components.
 * Association types are also persisted to localStorage (via `initialData`) so
 * that the first render after a navigation/refresh can use the real labels
 * synchronously instead of flashing the derived fallback while waiting on the
 * network. The query still refetches in the background to pick up changes.
 *
 * @param teamId - Team ID
 * @returns React Query result with association types and derived lookup maps
 */
export function useAssociationTypes(teamId: number | undefined) {
  const {
    data: associationTypes = EMPTY_ASSOCIATION_TYPES,
    isLoading,
    isFetched,
    error,
    refetch,
  } = useQuery({
    queryKey: associationTypesQueryKey(teamId),
    queryFn: async () => {
      if (!teamId) {
        return [];
      }
      const client = createSchemaClient(teamId);
      return client.getAssociationTypes();
    },
    enabled: !!teamId,
    staleTime: 5 * 60 * 1000, // 5 minutes - custom types can change at runtime
    initialData: () => getPersistedAssociationTypes(teamId),
    initialDataUpdatedAt: () => 0,
  });

  // Persist successful responses to localStorage so subsequent navigations
  // (and reloads) can render the correct association labels synchronously.
  useEffect(() => {
    if (!teamId || !associationTypes.length) {
      return;
    }
    setPersistedAssociationTypes(teamId, associationTypes);
  }, [associationTypes, teamId]);

  // Build a lookup map for quick access: association_type_name -> target_entity_type_name
  const associationTypeMap = useMemo(
    () =>
      new Map<string, string>(
        associationTypes.map((assoc: AssociationType) => [
          assoc.association_type_name,
          assoc.target_entity_type_name,
        ])
      ),
    [associationTypes]
  );

  // Full association-type lookup. `associationTypeMap` only carries the target
  // TYPE name, which is empty for kind-based associations — callers that need to
  // resolve an icon must see the kind too.
  const associationTypeByName = useMemo(
    () =>
      new Map<string, AssociationType>(
        associationTypes.map((assoc: AssociationType) => [
          assoc.association_type_name,
          assoc,
        ])
      ),
    [associationTypes]
  );

  // Set of association type names whose target entity kind is 'stage'.
  // Replaces the hardcoded STAGE_ASSOCIATION_TYPES_SET for dynamic detection.
  const stageAssociationTypes = useMemo(
    () =>
      new Set(
        associationTypes
          .filter((a: AssociationType) => a.target_entity_kind_name === 'stage')
          .map((a: AssociationType) => a.association_type_name)
      ),
    [associationTypes]
  );

  // Maps source entity type → stage association type name.
  // Replaces hardcoded ENTITY_TYPE_TO_STAGE_ASSOC.
  const stageAssocForEntityType = useMemo(
    () =>
      new Map<string, string>(
        associationTypes
          .filter((a: AssociationType) => a.target_entity_kind_name === 'stage')
          .map((a: AssociationType) => [
            a.source_entity_type_name,
            a.association_type_name,
          ])
      ),
    [associationTypes]
  );

  // association_type_name -> human display label. Prefer the schema label
  // (populated for both system and custom types); fall back to the same
  // humanization column headers use, which only covers names matching the
  // 'ahoy_X_to_Y' convention. Names that don't match — custom
  // 'org_X_to_Y' association types, or any name without a '_to_' segment —
  // are intentionally left unchanged on a cold cache until the schema label
  // loads; this matches column-header semantics and is an accepted
  // trade-off, not a gap. Stable reference — it crosses a prop boundary
  // into CrmListLayout.
  const associationLabelsByName = useMemo(
    () =>
      Object.fromEntries(
        associationTypes.map((a: AssociationType) => [
          a.association_type_name,
          a.label || formatAssociationName(a.association_type_name),
        ])
      ) as Record<string, string>,
    [associationTypes]
  );

  return {
    associationTypes,
    associationTypeMap,
    /** Full lookup: association_type_name -> AssociationType */
    associationTypeByName,
    /** Set of association type names that target a stage entity kind */
    stageAssociationTypes,
    /** Maps source entity type → stage association type name */
    stageAssocForEntityType,
    /** association_type_name -> display label (schema label or humanized fallback) */
    associationLabelsByName,
    isLoading,
    /**
     * True once the association-types query has settled (React Query
     * `isFetched`) — even if it resolved from empty `initialData`. Lets callers
     * stay lenient with saved lookup columns while the fetch is still in flight
     * instead of dropping them against a transiently-empty list.
     */
    isFetched,
    error,
    refetch,
  };
}
